import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  TextField,
} from '@mui/material';
import type { PlanEntity } from '@/types/run-stage.types';
import { updateRunPlan } from '@services/runStageApi.service';
import { useRunStageStore } from '@/run-stage/store/runStageStore';

interface PlanSummaryProps {
  plan: PlanEntity;
  isReadOnly?: boolean;
}

export const PlanSummary: React.FC<PlanSummaryProps> = ({ plan, isReadOnly = false }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeChosen, setTimeChosen] = useState(plan.timeChosen ?? '');
  const refresh = useRunStageStore((s) => s.refresh);

  const handleSaveTimeChosen = async () => {
    if (!plan.runId) return;
    setIsSaving(true);
    setError(null);
    try {
      await updateRunPlan({ runId: plan.runId, timeChosen: timeChosen || null });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save time');
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Plan Summary
        </Typography>
        {plan.completed ? (
          <Chip label="Plan Completed" color="success" size="small" />
        ) : (
          <Chip label="In Progress" color="primary" size="small" variant="outlined" />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Time selection */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Planned Start Time
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            type="datetime-local"
            size="small"
            value={timeChosen ? timeChosen.slice(0, 16) : ''}
            onChange={(e) => setTimeChosen(e.target.value)}
            disabled={isReadOnly || plan.completed || isSaving}
            slotProps={{ htmlInput: { 'aria-label': 'Planned start time' } }}
            sx={{ width: 240 }}
          />
          {!isReadOnly && !plan.completed && (
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveTimeChosen}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Steps preview */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Plan Steps ({plan.steps?.length ?? 0})
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(plan.steps ?? []).map((step, idx) => (
                <TableRow key={`${step.stepId}-${step.cloneNumber ?? 0}`} hover>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {step.order ?? idx + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{step.stepName || step.stepId}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {step.subType || step.archetype || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={step.status || 'PENDING'}
                      size="small"
                      color={step.completed ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {(!plan.steps || plan.steps.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                      No steps defined
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Execution type */}
      {(plan.typeOfExecution || plan.startTime) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Execution Details
          </Typography>
          <Stack spacing={0.5}>
            {plan.typeOfExecution && (
              <Typography variant="body2">
                Execution Mode:{' '}
                <Chip
                  label={plan.typeOfExecution}
                  size="small"
                  sx={{ ml: 0.5 }}
                />
              </Typography>
            )}
            {plan.startTime && (
              <Typography variant="body2" color="text.secondary">
                Start: {new Date(plan.startTime).toLocaleString()}
              </Typography>
            )}
            {plan.endTime && (
              <Typography variant="body2" color="text.secondary">
                End: {new Date(plan.endTime).toLocaleString()}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}


    </Box>
  );
};
